export type SearchType =
  | 'animal'
  | 'members'
  | 'mating'
  | 'sale-catalogues'
  | 'sale-catalogues-detail-animals'
  | 'semen-catalogues-detail-animals'
  | 'sale-catalogues-notes'
  | 'downloads'
  | 'online-registrations';

const ANIMALS_SUBPATHS = new Set(['mating', 'ebv-percentages']);

/**
 * Society-scoped public database UI lives under /{org}/animals, /members, /sales (list + /sales/{id}), /external (no /search).
 */
const SOCIETY_PUBLIC_PREFIX =
  /^\/[^/]+\/(animals|members|sales|external)(\/|$)/;

/**
 * Derive internal SearchType from society public pathname.
 * Returns null if pathname is not under a known society public route.
 */
export function parsePathnameToSearchType(
  pathname: string | null,
): SearchType | null {
  if (!pathname || !SOCIETY_PUBLIC_PREFIX.test(pathname)) return null;

  const tailMatch = pathname.match(/^\/[^/]+\/(.+)$/);
  const segments = (tailMatch?.[1] ?? '').split('/').filter(Boolean);
  if (segments.length === 0) return null;

  const [a, b, c] = segments;

  if (a === 'animals') {
    if (segments.length === 1) return 'animal';
    if (segments.length === 2 && b === 'sales') return 'sale-catalogues';
    if (segments.length === 2 && b === 'mating') return 'mating';
    if (segments.length === 2 && b === 'ebv-percentages') return 'animal';
    if (segments.length === 2 && b && !ANIMALS_SUBPATHS.has(b)) return 'animal';
    return null;
  }

  if (a === 'members') return 'members';

  if (a === 'sales') {
    if (segments.length === 1) return 'sale-catalogues';
    if (segments.length === 2 && b) return 'sale-catalogues-detail-animals';
    if (segments.length === 3 && b && c === 'notes')
      return 'sale-catalogues-notes';
    return null;
  }

  if (a === 'external') {
    if (b === 'registrations') return 'online-registrations';
    if (b === 'downloads') return 'downloads';
  }

  return null;
}

/**
 * Build pathname + query for a society public URL. Preserves unrelated query keys (e.g. shared, filters).
 * Removes searchType and saleId from query when encoded in the path.
 */
export function pathForSearchType(
  orgCode: string,
  searchType: SearchType,
  existingParams?: URLSearchParams | null,
): { pathname: string; query: URLSearchParams } {
  const q = new URLSearchParams(existingParams?.toString() ?? '');
  q.delete('searchType');

  const base = `/${orgCode}`;

  switch (searchType) {
    case 'animal':
      q.delete('saleId');
      return { pathname: `${base}/animals`, query: q };
    case 'members':
      q.delete('saleId');
      return { pathname: `${base}/members`, query: q };
    case 'sale-catalogues':
      q.delete('saleId');
      return { pathname: `${base}/sales`, query: q };
    case 'mating':
      q.delete('saleId');
      return { pathname: `${base}/animals/mating`, query: q };
    case 'downloads':
      q.delete('saleId');
      return { pathname: `${base}/external/downloads`, query: q };
    case 'online-registrations':
      q.delete('saleId');
      return { pathname: `${base}/external/registrations`, query: q };
    case 'sale-catalogues-detail-animals':
    case 'semen-catalogues-detail-animals': {
      const saleId =
        existingParams?.get('saleId') ?? q.get('saleId') ?? undefined;
      q.delete('saleId');
      if (!saleId) {
        return { pathname: `${base}/sales`, query: q };
      }
      return { pathname: `${base}/sales/${saleId}`, query: q };
    }
    case 'sale-catalogues-notes': {
      const saleId =
        existingParams?.get('saleId') ?? q.get('saleId') ?? undefined;
      q.delete('saleId');
      if (!saleId) {
        return { pathname: `${base}/sales`, query: q };
      }
      return { pathname: `${base}/sales/${saleId}/notes`, query: q };
    }
    default:
      q.delete('saleId');
      return { pathname: `${base}/animals`, query: q };
  }
}

export function buildSocietySearchHref(
  orgCode: string,
  searchType: SearchType,
  existingParams?: URLSearchParams | null,
): string {
  const { pathname, query } = pathForSearchType(
    orgCode,
    searchType,
    existingParams,
  );
  const qs = query.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}
